"use client";

import { Calendar, type CalendarEvent } from "@repo/design-system-2/components/blocks/calendar";
import { Button } from "@repo/design-system-2/components/ui/button";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@repo/design-system-2/components/ui/drawer";
import { Skeleton } from "@repo/design-system-2/components/ui/skeleton";
import { cn } from "@repo/design-system-2/lib/utils";
import { IconAdjustmentsHorizontal, IconAlertTriangle } from "@tabler/icons-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { parseAsArrayOf, parseAsBoolean, parseAsString, useQueryStates } from "nuqs";
import { useCallback, useMemo, useState } from "react";
import { arrayParamKey, sameStringArray } from "@/lib/helpers/filter-params";
import { FeisFilterForm, type FeisFilters } from "../../_components/filter-form";
import { fetchFeiseannaCalendar } from "./actions";

const calendarSearchParams = {
    hasExam: parseAsBoolean.withDefault(false),
    majors: parseAsBoolean.withDefault(false),
    month: parseAsString.withDefault(""),
    region: parseAsArrayOf(parseAsString).withDefault([]),
    registrationOpen: parseAsBoolean.withDefault(false),
    search: parseAsString.withDefault(""),
};

const monthPattern = /^(\d{4})-(\d{2})$/;

/** Parses a YYYY-MM month param, falling back to the current month. */
function resolveMonth(monthParam: string): { month: number; year: number } {
    const match = monthParam.match(monthPattern);

    if (match) {
        const year = Number(match[1]);
        const month = Number(match[2]);
        if (year >= 1900 && year <= 2200 && month >= 1 && month <= 12) {
            return { month, year };
        }
    }

    const now = new Date();
    return { month: now.getMonth() + 1, year: now.getFullYear() };
}

export function CalendarBrowser() {
    const [params, setParams] = useQueryStates(calendarSearchParams);
    const [filtersOpen, setFiltersOpen] = useState(false);

    const { month, year } = resolveMonth(params.month);
    const regionKey = arrayParamKey(params.region);
    const filters = useMemo(
        (): FeisFilters => ({
            hasExam: params.hasExam,
            regionIds: params.region,
            registrationOpen: params.registrationOpen,
            search: params.search,
            sort: "date",
            worldQualifier: params.majors,
        }),
        [params.hasExam, params.majors, params.registrationOpen, params.search, regionKey]
    );

    const queryFilters = useMemo(
        () => ({
            hasExam: params.hasExam,
            month,
            regionIds: params.region,
            registrationOpen: params.registrationOpen,
            search: params.search,
            worldQualifier: params.majors,
            year,
        }),
        [month, params.hasExam, params.majors, params.registrationOpen, params.search, regionKey, year]
    );

    const { data, error, isPending, isPlaceholderData } = useQuery({
        placeholderData: keepPreviousData,
        queryFn: async () => {
            const result = await fetchFeiseannaCalendar({
                hasExam: queryFilters.hasExam,
                month: queryFilters.month,
                regionIds: queryFilters.regionIds,
                registrationOpen: queryFilters.registrationOpen,
                search: queryFilters.search,
                worldQualifier: queryFilters.worldQualifier,
                year: queryFilters.year,
            });
            if (!result?.data) {
                throw new Error(result?.serverError ?? "Unable to load feiseanna.");
            }
            return result.data;
        },
        queryKey: ["feiseanna-calendar", queryFilters],
    });

    const handleFiltersChange = useCallback(
        (nextFilters: FeisFilters) => {
            if (
                nextFilters.search === params.search &&
                nextFilters.hasExam === params.hasExam &&
                nextFilters.registrationOpen === params.registrationOpen &&
                nextFilters.worldQualifier === params.majors &&
                sameStringArray(nextFilters.regionIds, params.region)
            ) {
                return;
            }

            setParams({
                hasExam: nextFilters.hasExam,
                majors: nextFilters.worldQualifier,
                region: nextFilters.regionIds,
                registrationOpen: nextFilters.registrationOpen,
                search: nextFilters.search,
            });
        },
        [params.hasExam, params.majors, params.region, params.registrationOpen, params.search, setParams]
    );

    function hrefForMonth(targetYear: number, targetMonth: number) {
        const query = new URLSearchParams();
        query.set("month", `${targetYear}-${String(targetMonth).padStart(2, "0")}`);

        if (params.search) {
            query.set("search", params.search);
        }
        if (params.region.length > 0) {
            query.set("region", params.region.join(","));
        }
        if (params.registrationOpen) {
            query.set("registrationOpen", "true");
        }
        if (params.hasExam) {
            query.set("hasExam", "true");
        }
        if (params.majors) {
            query.set("majors", "true");
        }

        return `/feiseanna/calendar?${query.toString()}`;
    }

    function calendarDom() {
        if (isPending) {
            return <CalendarSkeleton />;
        }

        if (error) {
            return (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                    <IconAlertTriangle className="mb-4 size-16 text-destructive opacity-80" />
                    <p className="font-heading font-semibold text-lg">Unable to load feiseanna.</p>
                    <p className="mt-1 text-muted-foreground text-sm">Please try again in a moment.</p>
                </div>
            );
        }

        const events: CalendarEvent[] = (data?.feisDates ?? []).map((row) => ({
            badge: row.regionAbbreviation ?? undefined,
            color: row.regionColor,
            date: row.date,
            href: `/feiseanna/${row.seriesLexeme}/${row.eventShortCode}`,
            id: row.eventId,
            label: row.seriesLabel,
        }));

        return (
            <Calendar
                className={cn(
                    "transition-opacity md:min-h-[32rem]",
                    isPlaceholderData && "opacity-60"
                )}
                events={events}
                hrefForMonth={hrefForMonth}
                month={month}
                year={year}
            />
        );
    }

    return (
        <div>
            <div className="hidden md:block">
                <FeisFilterForm
                    id="feis-calendar-filters-desktop"
                    initialFilters={filters}
                    layout="row"
                    onFiltersChange={handleFiltersChange}
                    showExam
                    showQualifier={false}
                    showSearch={false}
                    showSort={false}
                />
            </div>

            <div className="md:hidden">
                <Button onClick={() => setFiltersOpen(true)} variant="outline">
                    <IconAdjustmentsHorizontal />
                    Filters
                </Button>
                <Drawer modal={false} onOpenChange={setFiltersOpen} open={filtersOpen} showSwipeHandle>
                    <DrawerContent>
                        <DrawerHeader>
                            <DrawerTitle>Filters</DrawerTitle>
                        </DrawerHeader>
                        <div className="overflow-y-auto p-4">
                            {filtersOpen && (
                                <FeisFilterForm
                                    id="feis-calendar-filters-mobile"
                                    initialFilters={filters}
                                    layout="column"
                                    onFiltersChange={handleFiltersChange}
                                    showExam
                                    showQualifier={false}
                                    showSearch={false}
                                    showSort={false}
                                />
                            )}
                        </div>
                    </DrawerContent>
                </Drawer>
            </div>

            <div className="mt-4">{calendarDom()}</div>
        </div>
    );
}

function CalendarSkeleton() {
    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <Skeleton className="h-7 w-40 rounded-md" />
                <div className="flex items-center gap-1">
                    <Skeleton className="size-8 rounded-3xl" />
                    <Skeleton className="size-8 rounded-3xl" />
                    <Skeleton className="h-8 w-16 rounded-3xl" />
                    <Skeleton className="size-8 rounded-3xl" />
                    <Skeleton className="size-8 rounded-3xl" />
                </div>
            </div>
            <div className="hidden grid-cols-7 gap-px md:grid">
                {Array.from({ length: 35 }, (_, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: Static skeleton grid.
                    <Skeleton className="h-24 rounded-none" key={index} />
                ))}
            </div>
            <div className="flex flex-col gap-2 md:hidden">
                {Array.from({ length: 6 }, (_, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: Static skeleton list.
                    <Skeleton className="h-20 rounded-2xl" key={index} />
                ))}
            </div>
        </div>
    );
}

export function CalendarBrowserSkeleton() {
    return (
        <div>
            <div className="hidden flex-row flex-wrap items-end gap-4 md:flex">
                <div className="flex w-64 flex-col gap-2">
                    <Skeleton className="h-4 w-16 rounded-md" />
                    <Skeleton className="h-9 w-full rounded-3xl" />
                </div>
                <div className="flex w-48 flex-col gap-2">
                    <Skeleton className="h-4 w-16 rounded-md" />
                    <Skeleton className="h-9 w-full rounded-3xl" />
                </div>
                <div className="flex flex-row items-center gap-2 pb-2">
                    <Skeleton className="h-5 w-11 rounded-full" />
                    <Skeleton className="h-4 w-28 rounded-md" />
                </div>
                <div className="flex flex-row items-center gap-2 pb-2">
                    <Skeleton className="h-5 w-11 rounded-full" />
                    <Skeleton className="h-4 w-24 rounded-md" />
                </div>
                <div className="flex flex-row items-center gap-2 pb-2">
                    <Skeleton className="h-5 w-11 rounded-full" />
                    <Skeleton className="h-4 w-36 rounded-md" />
                </div>
            </div>

            <div className="md:hidden">
                <Skeleton className="h-9 w-24 rounded-3xl" />
            </div>

            <div className="mt-4">
                <CalendarSkeleton />
            </div>
        </div>
    );
}
